import { useEffect } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import PostTypesSection from './components/PostTypesSection';
import TemplateGrid from './components/TemplateGrid';
import ReelsSection from './components/ReelsSection';
import DifferentiatorSection from './components/DifferentiatorSection';
import HowItWorks from './components/HowItWorks';
import AccessSteps from './components/AccessSteps';
import FAQSection from './components/FAQSection';
import AgentCTA from './components/AgentCTA';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaProduct = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'The Realtor Content Engine',
  description:
    'The complete 30-day Instagram content system for real estate agents — done-for-you templates, captions, reel hook scripts, and a full posting calendar. Fully editable in Canva.',
  url: `${SITE_URL}/`,
  brand: {
    '@type': 'Brand',
    name: 'Realtor Content Engine',
  },
  offers: {
    '@type': 'Offer',
    price: '29.00',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: 'https://consultix6.gumroad.com/l/realtor-content-engine',
    seller: {
      '@type': 'Organization',
      name: 'Realtor Content Engine',
    },
  },
  image: `${SITE_URL}/og-image.jpg`,
  category: 'Digital Product',
};

const schemaWebSite = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Realtor Content Engine',
  url: `${SITE_URL}/`,
  description:
    'Stop wondering what to post. The Realtor Content Engine gives real estate agents 30 done-for-you Instagram templates, captions, reel hook scripts, and a full 30-day content calendar.',
  publisher: {
    '@type': 'Organization',
    name: 'Realtor Content Engine',
    url: `${SITE_URL}/`,
  },
};

const schemaFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What exactly do I get when I purchase?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You get the complete Realtor Content Engine bundle delivered instantly: 30 Canva post templates, 15 Canva story templates, a 30-day content calendar, 30 done-for-you captions, 30 reel hook scripts, 15 story prompts, and a 10-point profile & bio checklist. Everything is delivered via email as a Google Drive link and a Canva template access link — no waiting, no shipping.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need a paid Canva account to use this?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No — a free Canva account works perfectly. All templates are built to be fully accessible and editable on Canva\'s free tier. You can swap your photo, name, brokerage, and brand colors without needing Canva Pro.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I customize the templates with my branding?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It\'s a simple 4-step process that takes about 30 minutes to set up your entire month. Step 1: Click the Canva access link in your delivery email. Step 2: Click "Use Template" to create your own editable copy. Step 3: Replace the placeholder photo, name, brokerage, and colors with yours. Step 4: Download as a JPG or PNG and post directly to Instagram.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does the 30-day content calendar work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The calendar maps out every single day — what post type to publish, which caption to use, what format (Feed Post, Reel, Carousel, or Story), and the best time to post. Day 1 through Day 30 is fully planned.',
      },
    },
    {
      '@type': 'Question',
      name: "I don't have listings right now. Can I still use this?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely — and this is actually the best time to use it. Most of the 30 post types are not listing-dependent. Buyer tips, seller tips, market updates, neighborhood spotlights, client testimonials, meet the agent posts, and reel hooks all work without an active listing.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I use this for markets outside of Houston?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — 100%. All location references in the templates and captions are in brackets, so you just replace "Houston, TX" with your city. The content strategy, post types, captions, and reel hooks are built for any real estate market in the US.',
      },
    },
    {
      '@type': 'Question',
      name: "What's the difference between this and other template packs on Etsy?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most template packs give you the design and stop there. The Realtor Content Engine includes 30 written captions, 30 reel hook scripts, a full 30-day calendar, and story prompts with sticker strategies. It\'s a complete content system, not just a visual asset pack.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a refund policy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Because this is an instant-access digital product, all sales are final once the download link is delivered.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I receive the product after I buy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Instantly. The moment your payment goes through, Gumroad automatically emails you a download link. No waiting, no manual delivery. You\'ll have everything in your inbox within 60 seconds of purchase.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need design experience to use this?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Zero design experience needed. Every template opens directly in Canva — the free version. You click on the placeholder, type your information, and download.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I use these templates on Facebook and LinkedIn too?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. While the templates are sized for Instagram, the captions, reel hooks, and content strategy work across all platforms. Many agents repurpose the same content across Instagram, Facebook, and LinkedIn with zero extra effort.',
      },
    },
  ],
};

function injectSchema(id: string, data: object) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    (el as HTMLScriptElement).type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function HomePage() {
  useEffect(() => {
    injectSchema('schema-product', schemaProduct);
    injectSchema('schema-website', schemaWebSite);
    injectSchema('schema-faq', schemaFAQ);
    return () => {
      ['schema-product', 'schema-website', 'schema-faq'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <FeaturesSection />
      <PostTypesSection />
      <TemplateGrid />
      <DifferentiatorSection />
      <ReelsSection />
      <HowItWorks />
      <AccessSteps />
      <FAQSection />
      <AgentCTA />
      <ContactSection />
      <Footer />
    </div>
  );
}
